/**
 * Normalise rematch API failures into a clean Error whose message is safe to
 * show in a notification.
 *
 * The backend returns 409 when the invoice was updated concurrently (after
 * server-side retries) or when a duplicate rematch request is still in flight.
 * Axios rejects on non-2xx, so the friendly server message arrives on
 * error.response.data.message — surface it and add a retry hint.
 */
export function normalizeRematchError(error, fallbackMessage) {
  if (error?.isConcurrencyConflict) return error

  const status = error?.response?.status
  const serverMessage =
    error?.response?.data?.message || error?.data?.message || null

  const isConflict =
    status === 409 ||
    /updated while matching|already being checked/i.test(serverMessage || '')

  if (isConflict) {
    const normalized = new Error(
      serverMessage ||
        'This invoice was updated while matching was being checked.',
    )
    normalized.isConcurrencyConflict = true
    normalized.statusCode = 409
    return normalized
  }

  if (serverMessage && error?.message !== serverMessage) {
    const normalized = new Error(serverMessage)
    normalized.cause = error
    return normalized
  }

  if (error instanceof Error) return error
  return new Error(fallbackMessage || 'Could not check matching again')
}

export function isRematchConcurrencyConflict(error) {
  return Boolean(error?.isConcurrencyConflict)
}
